import { createContext, useContext, useMemo, useState, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSetup } from '@/contexts/SetupContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useRewardsSystem } from '@/hooks/useRewardsSystem';
import { walkthroughService, WalkthroughState } from '@/services/walkthroughService';
import { WALKTHROUGH_JOURNEYS, WalkthroughStop, StopGate, WalkthroughJourneyKey } from '@/lib/walkthroughJourneys';
import { isRouteHiddenBySettings } from '@/lib/equipmentPages';
import { hasKitchenAccess } from '@/utils/departmentAccess';
import { scrollAppToTop } from '@/lib/scrollAppToTop';
import { settingsService } from '@/lib/services/settings';

const RUN_KEY = 'ldg-walkthrough-run';

interface WalkthroughContextValue {
  state: WalkthroughState | undefined;
  stops: WalkthroughStop[];
  run: { index: number } | null;
  currentStop: WalkthroughStop | null;
  shouldWelcome: boolean;
  startRun: () => void;
  nextStop: () => void;
  prevStop: () => void;
  canGoBack: boolean;
  finishRun: () => void;
  exitRun: () => void;
  dismiss: () => void;
  isEligible: boolean; // non-leader with the feature available (drives the menu item)
  journeyKey: WalkthroughJourneyKey; // which journey this user runs (welcome copy etc.)
}

const WalkthroughContext = createContext<WalkthroughContextValue | null>(null);

export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isFeatureEnabled } = useSubscription();
  const { isEnabled: rewardsEnabled, isLoading: rewardsLoading } = useRewardsSystem();
  // Same query key Layout uses — shared cache, no extra fetch.
  const { data: storeSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsService.getSettings,
    enabled: !!user
  });

  // Every role has a journey now, admins included (tour-only orientation).
  // The store setup wizard still owns a FOUNDING admin's onboarding — see the
  // isSetupActive guard on shouldWelcome below.
  const isAdminUser = Boolean(user && (user.isAdmin || user.role === 'admin'));
  // Store setup state (admin-scoped query inside SetupProvider — we mount
  // beneath it). While the store's setup wizard is active, it owns the
  // admin's onboarding; the orientation must not compete.
  const { isSetupActive } = useSetup();

  const { data: state } = useQuery({
    queryKey: ['walkthrough'],
    queryFn: walkthroughService.get,
    enabled: !!user,
    staleTime: 60_000
  });

  // The server assigns the journey at account creation; users who predate the
  // feature have no walkthrough doc, so fall back to their role/position for
  // the self-serve "Take the tour" path.
  const fallbackJourney = (): WalkthroughJourneyKey => {
    if (isAdminUser) return 'admin';
    const position = user?.position ?? '';
    if (/director/i.test(position)) return 'director';
    if (/leader|manager/i.test(position)) return 'leader';
    if (/trainer/i.test(position)) return 'trainer';
    return 'team_member';
  };
  const journeyKey: WalkthroughJourneyKey = state?.journey ?? fallbackJourney();

  // Resolve the journey to this store's reality BEFORE the run, so the
  // itinerary and stop counts are honest.
  const stops = useMemo(() => {
    const journey = WALKTHROUGH_JOURNEYS[journeyKey];
    const gateOpen = (gate?: StopGate) => {
      if (!gate) return true;
      if (gate === 'rewards') return rewardsEnabled;
      if (gate === 'pathways') return isFeatureEnabled('leadership') && isFeatureEnabled('promotionPathways' as any);
      if (gate === 'kitchen_dept') {
        // Same semantics as the dashboard's kitchen-widget gating: BOH
        // category (canonical) or legacy/edge department names.
        const kitchenUser = hasKitchenAccess(user as any) ||
          user?.departments?.some((d: string) => ['Food Truck', 'Everything'].includes(d));
        return Boolean(isFeatureEnabled('kitchen') && kitchenUser);
      }
      // Store setting, not a subscription feature — mirrors Layout's menu gate.
      if (gate === 'safe_counting') return Boolean((storeSettings as any)?.safeConfig?.enabled);
      return isFeatureEnabled(gate as any);
    };
    return journey.stops
      .filter((s) => gateOpen(s.gate))
      // Pages the store switched off in Settings (the equipment pages)
      .filter((s) => !isRouteHiddenBySettings(s.route, storeSettings))
      .map((s) =>
        s.route === '__SELF_PROFILE__'
          ? { ...s, route: `/users/${user?._id}` }
          : s
      );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeatureEnabled, rewardsEnabled, user?._id, journeyKey, storeSettings]);

  const [run, setRun] = useState<{ index: number } | null>(() => {
    try {
      const raw = sessionStorage.getItem(RUN_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.index !== 'number' || parsed.index < 0) return null;
      return { index: parsed.index };
    } catch { return null; }
  });

  const persistRun = (next: { index: number } | null) => {
    setRun(next);
    if (next) sessionStorage.setItem(RUN_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(RUN_KEY);
  };

  const goTo = (index: number) => {
    const stop = stops[index];
    if (stop) { navigate(stop.route); scrollAppToTop(); }
  };

  // Optimistic: update the cache IMMEDIATELY so the UI responds on tap ("Not
  // now" felt dead waiting a full round-trip on phones), then PATCH. The
  // refetch must WAIT for the PATCH — invalidating first races the GET
  // against the write, which can resurrect stale 'pending' state. If the
  // PATCH fails, the refetch restores the truth.
  const patchThenRefresh = (status: 'in_progress' | 'completed' | 'dismissed') => {
    queryClient.setQueryData(['walkthrough'], (old: WalkthroughState | undefined) => ({ ...(old ?? {}), status }));
    walkthroughService.update(status)
      .catch(() => {})
      .finally(() => queryClient.invalidateQueries({ queryKey: ['walkthrough'] }));
  };

  const startRun = () => {
    if (!stops.length) return;
    // in_progress -> in_progress is not a legal transition server-side; only
    // PATCH when actually changing state.
    if (state?.status !== 'in_progress') patchThenRefresh('in_progress');
    // A deliberate re-run should replay the auto-tours.
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('ldg-wt-tour-'))
      .forEach((k) => sessionStorage.removeItem(k));
    persistRun({ index: 0 });
    goTo(0);
  };

  const nextStop = () => {
    if (!run) return;
    const next = run.index + 1;
    if (next >= stops.length) { finishRun(); return; }
    persistRun({ index: next });
    goTo(next);
  };

  const prevStop = () => {
    if (!run || run.index === 0) return;
    persistRun({ index: run.index - 1 });
    goTo(run.index - 1);
  };

  const finishRun = () => {
    patchThenRefresh('completed');
    persistRun(null);
    toast.success("That's the tour — welcome aboard! 🎉");
    navigate('/dashboard');
  };

  const exitRun = () => { persistRun(null); };

  const dismiss = () => {
    patchThenRefresh('dismissed');
  };

  // Never fight the store-setup wizard.
  const setupRunActive = !!sessionStorage.getItem('ldg-setup-run');

  // Gate sources load async and report disabled while loading — resolving the
  // journey before they settle would show a shrunken itinerary and could flip
  // "All done!" on early for a mid-run refresh.
  const gatesReady = !rewardsLoading && !settingsLoading;

  const value = useMemo<WalkthroughContextValue>(() => ({
    state, stops, run,
    currentStop: run && gatesReady ? stops[Math.min(run.index, stops.length - 1)] ?? null : null,
    shouldWelcome: gatesReady && state?.status === 'pending' && !run && !setupRunActive &&
      !(isAdminUser && isSetupActive),
    startRun, nextStop, prevStop,
    canGoBack: !!run && run.index > 0,
    finishRun, exitRun, dismiss,
    isEligible: !!user,
    journeyKey
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state, stops, run, isAdminUser, isSetupActive, setupRunActive, user, gatesReady, journeyKey]);

  return <WalkthroughContext.Provider value={value}>{children}</WalkthroughContext.Provider>;
}

export function useWalkthrough() {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) throw new Error('useWalkthrough must be used within WalkthroughProvider');
  return ctx;
}
